import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CountriesTerritoriesFacade } from '@cpak/main-features-lib/data-store';
import { StartService } from '../start/start.service';
import { BehaviorSubject, combineLatest, catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { getTerritoryList, sortByName } from '@cpak/shared/utils';
import { CountryDropdownItem, TerritoriesState } from '@cpak/interfaces';
import { CountriesTerritoriesAbstractService } from './countries-territories-abstract.service';
import { CountryConfigData, StartResponsePayload } from '@cpak/adp-cpak-bff-service-models';
import { TerritoriesService } from '../territories/territories.service';
import { MAX_TERRITORIES_LENGTH_WITHOUT_PAGINATION } from '@cpak/consts';
import { TranslocoService } from '@ngneat/transloco';
import { ALLCOUNTRY, allowRoles, ACCOUNT, CATALOG, COUNTRYARRAYLEN, MAINTENANCE } from './countries-const';

@Injectable({
  providedIn: 'root',
})
export class CountriesTerritoriesService implements CountriesTerritoriesAbstractService {
  private initUserRole: string | undefined;
  private countryLen: number | undefined;
  private countryState = new BehaviorSubject<string>(ALLCOUNTRY);
  private territoriesState = new BehaviorSubject<TerritoriesState>({ territories: [], allTerritories: true });

  constructor(
    private countriesTerritoriesFacade: CountriesTerritoriesFacade,
    private startService: StartService,
    private territoriesService: TerritoriesService,
    private translocoService: TranslocoService,
    private router: Router
  ) {
    this.startService.getStartResponse().subscribe(startResponse => {
      if (startResponse) {
        this.initUserRole = startResponse.data.userInfo.user.roles[0];
        this.countryLen = startResponse.data.userInfo.countryIds.length;
      }
    });
  }

  getCountryState(): Observable<string> {
    if (
      this.countryLen &&
      this.countryLen > COUNTRYARRAYLEN &&
      this.initUserRole &&
      allowRoles.indexOf(this.initUserRole) !== -1
    ) {
      return this.countryState.asObservable();
    }
    return this.countriesTerritoriesFacade.getSelectedCountries();
  }

  updateCountryState(countryCode: string): void {
    if (
      this.countryLen &&
      this.countryLen > COUNTRYARRAYLEN &&
      this.initUserRole &&
      allowRoles.indexOf(this.initUserRole) !== -1
    ) {
      this.countryState.next(countryCode);
      this.countriesTerritoriesFacade.updateCountryState(countryCode);
    }
    this.countriesTerritoriesFacade.updateCountryState(countryCode);
  }

  getTerritoriesState(): Observable<TerritoriesState> {
    // return this.territoriesState.asObservable();
    return this.countriesTerritoriesFacade.getSelectedTerritories();
  }

  updateTerritoriesState(state: TerritoriesState): void {
    // this.territoriesState.next(state);
    this.countriesTerritoriesFacade.updateTerritoriesState(state);
  }

  getAllCountries(): Observable<CountryDropdownItem[]> {
    return combineLatest([
      this.startService.getStartResponse(),
      this.translocoService.selectTranslate('allCountries').pipe(take(1)),
    ]).pipe(
      map(([startResponse, translation]) => {
        if (startResponse) {
          const countryConfig = startResponse?.data.config.countries || [];
          const availableCountries = (startResponse?.data.userInfo.countryIds || [])
            .map(countryCode => countryConfig.find(country => country.id === countryCode))
            .filter(Boolean)
            .map(country => ({ code: (country as CountryConfigData).id, name: (country as CountryConfigData).name }));
          if (
            this.countryLen &&
            this.countryLen > COUNTRYARRAYLEN &&
            this.initUserRole &&
            allowRoles.indexOf(this.initUserRole) !== -1
          ) {
            if (
              this.router.url.indexOf(ACCOUNT) !== -1 ||
              this.router.url.indexOf(CATALOG) !== -1 ||
              this.router.url.indexOf(MAINTENANCE) !== -1
            ) {
              return [...sortByName(availableCountries)];
            }
            return [...sortByName(availableCountries), { code: ALLCOUNTRY, name: translation }];
          }
          return [...sortByName(availableCountries)];
        }
        return [];
      })
    );
  }

  getTerritories(countryCode: string): Observable<CountryDropdownItem[]> {
    if (countryCode !== ALLCOUNTRY) {
      return this.startService.getStartResponse().pipe(
        switchMap(start =>
          (start?.data.userInfo.territories || []).length < MAX_TERRITORIES_LENGTH_WITHOUT_PAGINATION
            ? this.getTerritoriesFromStartConfig(countryCode, start)
            : this.getTerritoriesFromApi(countryCode, start)
        ),
        map(territories => sortByName(territories))
      );
    } else {
      return of([]);
    }
  }

  private getTerritoriesFromApi(
    countryCode: string,
    start: StartResponsePayload | null
  ): Observable<CountryDropdownItem[]> {
    const userId = start?.data.userInfo.user.id || '';
    return this.territoriesService.getUserTerritories(userId, countryCode).pipe(
      map(response =>
        (response?.data.territories || []).map(territory => ({ name: territory.name, code: territory.id }))
      ),
      catchError(() => [])
    );
  }

  private getTerritoriesFromStartConfig(
    countryCode: string,
    start: StartResponsePayload | null
  ): Observable<CountryDropdownItem[]> {
    const allTerritories = start?.data.userInfo.territories || [];
    const territoriesByCountry = sortByName(getTerritoryList(allTerritories, countryCode));
    return of(territoriesByCountry);
  }
}
